import { Agreement } from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import {
  AllLocalRecipients,
  AllRecipients,
  displayInfo,
  getContactType,
  getReceiverType,
} from 'features/v2a/sender/helpers/Sender.helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import theme from 'shared/styles/theme';
import {
  showZendeskArticle,
  zendeskArticleIds,
} from 'shared/utils/zendesk/zendeskUtils';

interface TransferConfirmBottomSheetViewProps {
  handleConfirmation: () => void;
  handleCheckAgreement: (isChecked: boolean) => void;
  agreementsChecked: boolean;
  agreement: Agreement | null;
  confirmationMessage: string | null;
  selectedRecipient?: AllRecipients | AllLocalRecipients;
  contactName: string;
  closeBottomSheet: () => void;
  fetching: boolean;
  transferAmount?: string;
  transferNote?: string;
}

const TransferConfirmBottomSheetView = ({
  handleConfirmation,
  handleCheckAgreement,
  agreementsChecked,
  agreement,
  confirmationMessage,
  transferAmount,
  selectedRecipient,
  contactName,
  closeBottomSheet,
  fetching,
}: TransferConfirmBottomSheetViewProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'sender',
  });
  const { bottom } = useSafeAreaInsets();

  return (
    <>
      <View style={styles.confirmTransferSheet}>
        <TitleMdText numberOfLines={0}>
          {`${translatedText('titleFirstPart')} $${transferAmount} ${translatedText('titleMiddlePart')} ${
            contactName || (selectedRecipient && displayInfo(selectedRecipient))
          }.`}
        </TitleMdText>
        <BodyBaseText accessibilityLabel="confirmation-text">
          <BodyBaseText>
            {confirmationMessage ??
              translatedText('v2aTransfer.confirmationText')}
          </BodyBaseText>
          <HyperlinkBaseText
            onPress={() => {
              showZendeskArticle(zendeskArticleIds.VARO_TO_ANYONE_RECEIVING);
              closeBottomSheet();
            }}
            analyticsId="v2AFaqClicked"
            customAttributes={{
              faq_name: 'v2a',
              cta: 'Learn more',
            }}
          >
            {translatedText('v2aTransfer.learnMore')}
          </HyperlinkBaseText>
        </BodyBaseText>
        {agreement?.id ? (
          <View style={styles.agreements}>
            <Checkbox
              labelText=""
              checked={agreementsChecked}
              onSelectedStateChange={() => {
                handleCheckAgreement(!agreementsChecked);
              }}
              testId="checkBoxClicked"
              analyticsId="v2ATermsAndConditionsAccepted"
            />
            <BodyBaseText style={styles.agreementText}>
              <BodyBaseText>{agreement.title} </BodyBaseText>
              <HyperlinkBaseText
                onPress={() => {
                  if (agreement.resource?.pdfDocument?.url) {
                    router.push({
                      pathname: '/move-money/v2a/sender-agreement',
                      params: {
                        pdfUrl: agreement.resource.pdfDocument.url,
                        headerTitle: agreement.title,
                      },
                    });
                  }
                  closeBottomSheet();
                }}
                analyticsId="v2ATermsAndConditionsAccessed"
              >
                {translatedText('termsAndConditions')}
              </HyperlinkBaseText>
            </BodyBaseText>
          </View>
        ) : null}
      </View>
      <View style={[styles.buttonGroup, { paddingBottom: bottom }]}>
        <Button
          label={translatedText('confirmButton')}
          isDisabled={Boolean(agreement?.id) && !agreementsChecked}
          type="PRIMARY"
          onPress={handleConfirmation}
          isLoading={fetching}
          analyticsId="v2ASubmitted"
          customAttributes={{
            receiver_id: selectedRecipient?.id
              ? String(selectedRecipient.id)
              : '',
            receiver_type: getReceiverType(selectedRecipient),
            amount: transferAmount
              ? transferAmount.replace('$', '').trim()
              : '',
            is_first: agreement?.id ? 'true' : 'false',
            contact_type: getContactType(selectedRecipient),
          }}
        />

        <Button
          label={translatedText('cancelButton')}
          type="SECONDARY"
          onPress={closeBottomSheet}
          analyticsId="v2ACancelled"
        />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  agreementText: {
    flexShrink: 1,
    flexWrap: 'wrap',
  },
  agreements: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  buttonGroup: {
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  confirmTransferSheet: {
    gap: theme.spacing.s,
    padding: theme.spacing.m,
  },
});

export default TransferConfirmBottomSheetView;
